import { useNavigate } from "react-router-dom";
import MenuTop from "../../../../components/menu/top/menu-top";
import { useEffect, useState, useCallback } from "react";
import Loader from "../../../../components/loader/loader";
import { useAlert } from "../../../../components/alert/alert";
import { SEVERITY } from "../../../../shared/interfaces/alert";
import Icons from "../../../../components/icons/iconts";
import storage from "../../../../shared/services/storage";
import { fetchCompaniesService } from "../../../../features/company/service/company";
import { fetchMonthlyPaymentsService } from "../service/service-monthly";
import SelectAutocomplete from "../../../../components/inputs/select/autocomplete/select-autocomplete";
import { 
    getMonthlyPaymentsStyles, 
    summaryCardBorders, 
    paymentStatusColors, 
    statusIndicatorColors 
} from "./style";

// Interface para os dados de pagamento
interface BeneficiaryPayment {
    id: string;
    beneficiary: {
        id: string;
        name: string;
        type: string;
    };
    project: {
        id: string;
        name: string;
        financierId?: string;
        principalInvestigatorId?: string;
    };
    monthly_cost: number;
    monthly_cost_display: number;
    description?: string;
    is_paid_this_month: boolean;
    paid_amount: number;
    paid_amount_display: number;
    paid_date?: string;
    payment_status: string;
    created_at: string;
    companyId?: string;
    companyName?: string;
}

interface CompanyPayments {
    companyId: string;
    companyName: string;
    totalBeneficiaries: number;
    totalMonthlyValue: number;
    totalPaid: number;
    totalPending: number;
    payments: BeneficiaryPayment[];
    isExpanded: boolean;
}

export function MonthlyPaymentsList() {
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const [isLoading, setIsLoading] = useState(false);
    const [companiesPayments, setCompaniesPayments] = useState<CompanyPayments[]>([]);
    const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
    const [userCompany, setUserCompany] = useState<any>(null);
    const [isCausale, setIsCausale] = useState<boolean>(false);
    const [selectedCompanyFilter, setSelectedCompanyFilter] = useState<string>('all');
    const [companies, setCompanies] = useState<any[]>([]);
    const [showPaidOnly, setShowPaidOnly] = useState<boolean>(false);
    const [showPendingOnly, setShowPendingOnly] = useState<boolean>(false);
    const [isMobile, setIsMobile] = useState<boolean>(false);

    // Detectar se é mobile
    useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);
        
        return () => window.removeEventListener('resize', checkIsMobile);
    }, []);

    // Obter estilos
    const styles = getMonthlyPaymentsStyles(isMobile);

    // Verificar se o usuário é da Causale
    const checkUserCompany = useCallback(async () => {
        try {
            const userString = await storage.getStorage('user');
            if (userString) {
                const userData = JSON.parse(userString);
                setUserCompany(userData.company);
                const isUserCausale = userData.company?.name?.toLowerCase() === 'causale';
                setIsCausale(isUserCausale);
                
                // Se não for Causale, definir o filtro para a empresa do usuário
                if (!isUserCausale && userData.company?.id) {
                    setSelectedCompanyFilter(userData.company.id);
                }
            }
        } catch (error) {
            console.error('Erro ao verificar empresa do usuário:', error);
            showAlert('Erro ao verificar dados do usuário', SEVERITY.ERROR);
        }
    }, [showAlert]);

    // Buscar lista de empresas (apenas para usuários Causale)
    const fetchCompanies = useCallback(async () => {
        if (!isCausale) return;
        
        try {
            const response = await fetchCompaniesService();
            setCompanies(Array.isArray(response) ? response : []);
        } catch (error) {
            console.error('Erro ao buscar empresas:', error);
            showAlert('Erro ao carregar lista de empresas', SEVERITY.ERROR);
        }
    }, [isCausale, showAlert]);

    // Buscar pagamentos mensais
    const fetchMonthlyPayments = useCallback(async () => {
        if (!userCompany) return;

        try {
            setIsLoading(true);
            
            const companyFilter = isCausale && selectedCompanyFilter !== 'all' ? selectedCompanyFilter : null;
            const data = await fetchMonthlyPaymentsService(
                selectedYear, 
                selectedMonth, 
                companyFilter
            );
            
            if (!data) {
                console.warn('Dados inválidos retornados:', data);
                setCompaniesPayments([]);
                return;
            }

            // Usar o beneficiariesByCompany e companiesMap que vem da API
            const beneficiariesByCompany = data.beneficiariesByCompany || {};
            const companiesMap = data.companiesMap || {};

            // Montar array de empresas com seus dados
            const companiesData: CompanyPayments[] = Object.entries(beneficiariesByCompany).map(([companyId, payments]: [string, any]) => {
                const paymentsArray = Array.isArray(payments) ? payments : [];
                const companyName = companiesMap[companyId] || 'Empresa Desconhecida';
                
                const totalPaid = paymentsArray.filter((p: any) => p.is_paid_this_month).length;
                const totalPending = paymentsArray.filter((p: any) => !p.is_paid_this_month).length;
                const totalValue = paymentsArray.reduce((sum: number, p: any) => sum + (p.monthly_cost_display || 0), 0);
                
                return {
                    companyId,
                    companyName,
                    totalBeneficiaries: paymentsArray.length,
                    totalMonthlyValue: totalValue,
                    totalPaid,
                    totalPending,
                    payments: paymentsArray,
                    isExpanded: false
                };
            });

            // Ordenar por nome da empresa
            companiesData.sort((a, b) => a.companyName.localeCompare(b.companyName));

            setCompaniesPayments(companiesData);
            
        } catch (error: any) {
            console.error('Erro ao buscar pagamentos:', error);
            showAlert(error.message || 'Erro ao carregar pagamentos mensais', SEVERITY.ERROR);
            setCompaniesPayments([]);
        } finally {
            setIsLoading(false);
        }
    }, [selectedMonth, selectedYear, selectedCompanyFilter, userCompany, isCausale, showAlert]);

    // Alternar expansão de empresa
    const toggleCompanyExpansion = (companyId: string) => {
        setCompaniesPayments(prev => 
            prev.map(company => 
                company.companyId === companyId 
                    ? { ...company, isExpanded: !company.isExpanded }
                    : company
            )
        );
    };

    // Gerar opções de meses para SelectAutocomplete
    const monthOptions = [
        { id: '1', value: '1', label: isMobile ? 'Jan' : 'Janeiro' },
        { id: '2', value: '2', label: isMobile ? 'Fev' : 'Fevereiro' },
        { id: '3', value: '3', label: isMobile ? 'Mar' : 'Março' },
        { id: '4', value: '4', label: isMobile ? 'Abr' : 'Abril' },
        { id: '5', value: '5', label: isMobile ? 'Mai' : 'Maio' },
        { id: '6', value: '6', label: isMobile ? 'Jun' : 'Junho' },
        { id: '7', value: '7', label: isMobile ? 'Jul' : 'Julho' },
        { id: '8', value: '8', label: isMobile ? 'Ago' : 'Agosto' },
        { id: '9', value: '9', label: isMobile ? 'Set' : 'Setembro' },
        { id: '10', value: '10', label: isMobile ? 'Out' : 'Outubro' },
        { id: '11', value: '11', label: isMobile ? 'Nov' : 'Novembro' },
        { id: '12', value: '12', label: isMobile ? 'Dez' : 'Dezembro' }
    ];

    // Gerar opções de anos para SelectAutocomplete
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 5 }, (_, i) => {
        const year = currentYear - 2 + i;
        return {
            id: year.toString(),
            value: year.toString(),
            label: year.toString()
        };
    });

    // Gerar opções de empresas para SelectAutocomplete
    const companyOptions = [
        { id: 'all', value: 'all', label: 'Todas as Empresas' },
        ...companies.map(company => ({
            id: company.id,
            value: company.id,
            label: company.name
        }))
    ];

    // Navegar para o próximo mês
    const goToNextMonth = () => {
        if (selectedMonth === 12) {
            setSelectedMonth(1);
            setSelectedYear(selectedYear + 1);
        } else {
            setSelectedMonth(selectedMonth + 1);
        }
    };

    // Navegar para o mês anterior
    const goToPreviousMonth = () => {
        if (selectedMonth === 1) {
            setSelectedMonth(12);
            setSelectedYear(selectedYear - 1);
        } else {
            setSelectedMonth(selectedMonth - 1);
        }
    };

    // Filtrar pagamentos
    const getFilteredPayments = (payments: BeneficiaryPayment[]) => {
        let filtered = [...payments];
        
        if (showPaidOnly) {
            filtered = filtered.filter(p => p.is_paid_this_month);
        }
        
        if (showPendingOnly) {
            filtered = filtered.filter(p => !p.is_paid_this_month);
        }
        
        return filtered;
    };

    // Calcular totais gerais
    const calculateTotals = () => {
        const totals = {
            companies: companiesPayments.length,
            beneficiaries: 0,
            totalValue: 0,
            totalPaid: 0,
            totalPending: 0
        };

        companiesPayments.forEach(company => {
            totals.beneficiaries += company.totalBeneficiaries;
            totals.totalValue += company.totalMonthlyValue;
            totals.totalPaid += company.totalPaid;
            totals.totalPending += company.totalPending;
        });

        return totals;
    };

    // Função para formatar data de forma segura
    const formatDate = (dateString?: string) => {
        if (!dateString) return 'data não registrada';
        try {
            return new Date(dateString).toLocaleDateString('pt-BR');
        } catch {
            return 'data inválida';
        }
    };

    useEffect(() => {
        checkUserCompany();
    }, [checkUserCompany]);

    useEffect(() => {
        if (userCompany) {
            fetchCompanies();
        }
    }, [userCompany, fetchCompanies]);

    useEffect(() => {
        if (userCompany) {
            fetchMonthlyPayments();
        }
    }, [selectedMonth, selectedYear, selectedCompanyFilter, fetchMonthlyPayments]);

    const totals = calculateTotals();

    return (
        <div className="container-client">
            <MenuTop />
            {isLoading && <Loader />}
            
            <div className='container-client__content' style={styles.container}>
                <div className="container-client__content__title">
                    <h1 className="container-client__content__title-text" style={styles.title}>
                        {isMobile ? 'Pagamentos Mensais' : 'Pagamentos Mensais por Empresa'}
                    </h1>
                </div>
                
                {/* Controles de navegação e filtros */}
                <div style={styles.controlsContainer}>
                    {/* Navegação de mês */}
                    <div style={styles.monthNavigation}>
                        <button 
                            onClick={goToPreviousMonth}
                            style={styles.navButton}
                        >
                            <Icons iconName="ChevronLeft" fontSize={isMobile ? 18 : 20} color="white" />
                        </button>
                        
                        <div style={styles.selectContainer}>
                            <SelectAutocomplete
                                data={monthOptions}
                                value={monthOptions.find(m => m.id === selectedMonth.toString())}
                                onChange={(value: any) => setSelectedMonth(Number(value))}
                                placeholder={isMobile ? "Mês" : "Selecione o mês"}
                                width={isMobile ? 120 : 140}
                                height={isMobile ? 50 : 55}
                            />
                            
                            <SelectAutocomplete
                                data={yearOptions}
                                value={yearOptions.find(y => y.id === selectedYear.toString())}
                                onChange={(value: any) => setSelectedYear(Number(value))}
                                placeholder="Ano"
                                width={isMobile ? 80 : 130}
                                height={isMobile ? 50 : 55}
                            />
                        </div>
                        
                        <button 
                            onClick={goToNextMonth}
                            style={styles.navButton}
                        >
                            <Icons iconName="ChevronRight" fontSize={isMobile ? 18 : 20} color="white" />
                        </button>
                    </div>

                    {/* Filtro de empresa (apenas para Causale) */}
                    {isCausale && companies.length > 0 && (
                        <SelectAutocomplete
                            data={companyOptions}
                            value={companyOptions.find(c => c.id === selectedCompanyFilter)}
                            onChange={(value: any) => setSelectedCompanyFilter(value || 'all')}
                            placeholder="Selecione a empresa"
                            width={isMobile ? 300 : 320}
                            height={isMobile ? 50 : 55}
                        />
                    )}

                    {/* Filtros de status */}
                    <div style={styles.statusFiltersContainer}>
                        <label style={styles.filterLabel}>
                            <input
                                type="checkbox"
                                checked={showPaidOnly}
                                onChange={(e) => {
                                    setShowPaidOnly(e.target.checked);
                                    if (e.target.checked) setShowPendingOnly(false);
                                }}
                                style={styles.filterCheckbox}
                            />
                            {isMobile ? 'Pagos' : 'Apenas Pagos'}
                        </label>
                        
                        <label style={styles.filterLabel}>
                            <input
                                type="checkbox"
                                checked={showPendingOnly}
                                onChange={(e) => {
                                    setShowPendingOnly(e.target.checked);
                                    if (e.target.checked) setShowPaidOnly(false);
                                }}
                                style={styles.filterCheckbox}
                            />
                            {isMobile ? 'Pendentes' : 'Apenas Pendentes'}
                        </label>
                    </div>
                </div>

                {/* Lista de empresas e pagamentos */}
                {companiesPayments.length > 0 ? (
                    <div style={styles.companiesListContainer}>
                        {companiesPayments.map(company => {
                            const filteredPayments = getFilteredPayments(company.payments);
                            
                            return (
                                <div key={company.companyId} style={styles.companyCard}>
                                    {/* Cabeçalho da empresa */}
                                    <div
                                        onClick={() => toggleCompanyExpansion(company.companyId)}
                                        style={{
                                            ...styles.companyHeader,
                                            borderBottom: company.isExpanded ? '1px solid #dee2e6' : 'none'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#e9ecef';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                                        }}
                                    >
                                        <div style={styles.companyHeaderMain}>
                                            <Icons 
                                                iconName={company.isExpanded ? "ExpandLess" : "ExpandMore"} 
                                                fontSize={isMobile ? 20 : 24} 
                                                color="#495057" 
                                            />
                                            <div style={styles.companyInfo}>
                                                <h3 style={styles.companyName}>
                                                    {company.companyName}
                                                </h3>
                                                <div style={styles.companyDetails}>
                                                    {company.totalBeneficiaries} beneficiário(s) • 
                                                    {' '}{company.totalMonthlyValue.toLocaleString('pt-BR', {
                                                        style: 'currency',
                                                        currency: 'BRL'
                                                    })}/mês
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div style={styles.statusBadgesContainer}>
                                            <span style={styles.paidBadge}>
                                                {company.totalPaid} pago(s)
                                            </span>
                                            <span style={styles.pendingBadge}>
                                                {company.totalPending} pendente(s)
                                            </span>
                                        </div>
                                    </div>

                                    {/* Lista de pagamentos expandida */}
                                    {company.isExpanded && (
                                        <div style={styles.paymentsContainer}>
                                            {filteredPayments.length > 0 ? (
                                                <div style={styles.paymentsList}>
                                                    {filteredPayments.map(payment => (
                                                        <div
                                                            key={payment.id}
                                                            style={styles.paymentItem}
                                                            onClick={() => navigate(`/beneficiario-projeto/editar/${payment.id}`)}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.transform = 'translateY(0)';
                                                                e.currentTarget.style.boxShadow = 'none';
                                                            }}
                                                        >
                                                            <div style={styles.beneficiaryInfo}>
                                                                <div style={styles.beneficiaryName}>
                                                                    {payment.beneficiary?.name || 'Nome não disponível'}
                                                                </div>
                                                                <div style={styles.projectName}>
                                                                    Projeto: {payment.project?.name || 'Projeto não informado'}
                                                                </div>
                                                                {payment.description && (
                                                                    <div style={styles.description}>
                                                                        {payment.description}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            
                                                            <div style={styles.valueStatusContainer}>
                                                                <div style={styles.valueContainer}>
                                                                    <div style={styles.mainValue}>
                                                                        {(payment.monthly_cost_display || 0).toLocaleString('pt-BR', {
                                                                            style: 'currency',
                                                                            currency: 'BRL'
                                                                        })}
                                                                    </div>
                                                                    {payment.is_paid_this_month ? (
                                                                        <div style={{
                                                                            ...styles.paymentStatus,
                                                                            color: paymentStatusColors.paid
                                                                        }}>
                                                                            Pagamento efetuado
                                                                        </div>
                                                                    ) : (
                                                                        <div style={{
                                                                            ...styles.paymentStatus,
                                                                            color: paymentStatusColors.pending
                                                                        }}>
                                                                            Pagamento pendente
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                
                                                                <div style={{
                                                                    ...styles.statusIndicator,
                                                                    backgroundColor: payment.is_paid_this_month 
                                                                        ? statusIndicatorColors.paid 
                                                                        : statusIndicatorColors.pending
                                                                }} />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div style={styles.noPaymentsMessage}>
                                                    {company.totalBeneficiaries === 0 
                                                        ? 'Nenhum beneficiário cadastrado para esta empresa neste período'
                                                        : 'Nenhum pagamento encontrado com os filtros aplicados'
                                                    }
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    !isLoading && (
                        <div style={styles.emptyState}>
                            <Icons iconName="EventNote" fontSize={isMobile ? 48 : 64} color="#dee2e6" />
                            <h3 style={styles.emptyStateTitle}>
                                Nenhuma empresa encontrada
                            </h3>
                            <p style={styles.emptyStateDescription}>
                                Não há empresas com pagamentos programados para {monthOptions.find(m => m.id === selectedMonth.toString())?.label} de {selectedYear}
                            </p>
                            <button
                                onClick={() => navigate('/beneficiario-projeto/listar')}
                                style={styles.emptyStateButton}
                            >
                                Ver Todos os Vínculos
                            </button>
                        </div>
                    )
                )}
            </div>
        </div>
    );
}